import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  where,
} from "firebase/firestore";

import { db } from "@/apis/config/firebase";
import type {
  ImagingTrackingNotification,
  SharedRecord,
} from "@/features/imagings/schemas/imagings";

import type { DocumentSnapshot } from "firebase/firestore";

/**
 * Servicio para manejar operaciones de tracking notifications en Firebase
 */
export class TrackingService {
  /**
   * Obtiene tracking notifications con paginación
   */
  static async getTrackingNotifications(
    imagingId: string,
    lastDoc?: DocumentSnapshot,
    itemsPerPage: number = 5
  ): Promise<{
    notifications: ImagingTrackingNotification[];
    lastDoc: DocumentSnapshot | null;
  }> {
    try {
      let trackingQuery = query(
        collection(db, "tracking_notifications"),
        where("imaging", "==", imagingId),
        orderBy("created_at", "desc"),
        limit(itemsPerPage)
      );

      if (lastDoc) {
        trackingQuery = query(
          collection(db, "tracking_notifications"),
          where("imaging", "==", imagingId),
          orderBy("created_at", "desc"),
          startAfter(lastDoc),
          limit(itemsPerPage)
        );
      }

      const trackingSnapshot = await getDocs(trackingQuery);

      const notifications = trackingSnapshot.docs.map(
        (doc) => doc.data() as ImagingTrackingNotification
      );

      return {
        notifications,
        lastDoc: trackingSnapshot.docs[trackingSnapshot.docs.length - 1] || null,
      };
    } catch (error) {
      console.error("Error al obtener tracking notifications:", error);
      throw new Error("Error al obtener tracking notifications");
    }
  }

  /**
   * Obtiene registros compartidos con paginación
   * @deprecated - Ahora se usa getTrackingNotifications como fuente principal
   */
  static async getSharedRecords(
    imagingId: string,
    lastDoc?: DocumentSnapshot,
    itemsPerPage: number = 5
  ): Promise<{
    records: SharedRecord[];
    lastDoc: DocumentSnapshot | null;
  }> {
    try {
      let sharedQuery = query(
        collection(db, "shared_studies"),
        where("imaging", "==", imagingId),
        orderBy("created_at", "desc"),
        limit(itemsPerPage)
      );

      if (lastDoc) {
        sharedQuery = query(
          collection(db, "shared_studies"),
          where("imaging", "==", imagingId),
          orderBy("created_at", "desc"),
          startAfter(lastDoc),
          limit(itemsPerPage)
        );
      }

      const sharedSnapshot = await getDocs(sharedQuery);

      const records = sharedSnapshot.docs.map((doc) => ({
        ...(doc.data() as SharedRecord),
        id: doc.id,
      }));

      return {
        records,
        lastDoc: sharedSnapshot.docs[sharedSnapshot.docs.length - 1] || null,
      };
    } catch (error) {
      console.error("Error al obtener registros compartidos:", error);
      throw new Error("Error al obtener registros compartidos");
    }
  }

  /**
   * Obtiene el conteo total de registros compartidos
   * @deprecated - Ahora se usa getTrackingNotificationsCount como fuente principal
   */
  static async getSharedRecordsCount(imagingId: string): Promise<number> {
    try {
      const sharedQuery = query(
        collection(db, "shared_studies"),
        where("imaging", "==", imagingId)
      );

      const sharedSnapshot = await getDocs(sharedQuery);
      return sharedSnapshot.size;
    } catch (error) {
      console.error("Error al obtener el conteo total de registros compartidos:", error);
      throw new Error("Error al obtener el conteo total de registros compartidos");
    }
  }

  /**
   * Obtiene el conteo total de tracking notifications
   */
  static async getTrackingNotificationsCount(imagingId: string): Promise<number> {
    try {
      const trackingQuery = query(
        collection(db, "tracking_notifications"),
        where("imaging", "==", imagingId)
      );

      const trackingSnapshot = await getDocs(trackingQuery);
      return trackingSnapshot.size;
    } catch (error) {
      console.error("Error al obtener conteo de tracking notifications:", error);
      throw new Error("Error al obtener conteo de tracking notifications");
    }
  }

  /**
   * Obtiene estadísticas de tracking notifications
   * Ahora usa tracking como fuente principal
   */
  static async getTrackingStats(imagingId: string) {
    const trackingNotificationsQuery = await getDocs(
      query(collection(db, "tracking_notifications"), where("imaging", "==", imagingId))
    );

    return {
      sharedStudies: trackingNotificationsQuery.size, // Usar tracking como fuente principal
      trackingNotifications: trackingNotificationsQuery.size,
    };
  }
}
